import { useId } from 'react'

import { cn } from '@/lib/utils'
import { ScreenFieldItem } from './ScreenFieldItem'
import {
  type Answers,
  rowContext,
  rowErrorKey,
  rowFieldsFor,
  type ScreenErrors,
  type ScreenField,
  ScreenFieldKind,
  type ScreenValue,
  type ScreenValues,
  visibleFields,
} from './screenForm'
import type { ScreenForm } from './useScreenForm'

interface ScreenFieldsProps {
  form: ScreenForm
  disabled?: boolean
  className?: string
}

/** The visible fields of a screen, stacked, each with its label, help and error. */
export function ScreenFields({ form, disabled, className }: ScreenFieldsProps) {
  const idPrefix = useId()
  return (
    <div ref={form.containerRef} className={cn('flex w-full flex-col gap-6', className)}>
      <FieldList
        fields={form.fields}
        values={form.values}
        onChange={form.setValue}
        errors={form.errors}
        clearError={form.clearError}
        errorPrefix=""
        idPrefix={idPrefix}
        disabled={disabled}
      />
    </div>
  )
}

interface FieldListProps {
  fields: ScreenField[]
  values: ScreenValues
  context?: Answers
  onChange: (key: string, value: ScreenValue) => void
  errors: ScreenErrors
  clearError: (key: string) => void
  /** Prepended to each field key to find its error: '' at the top, the row's prefix inside a row. */
  errorPrefix: string
  idPrefix: string
  disabled?: boolean
}

function FieldList({
  fields,
  values,
  context,
  onChange,
  errors,
  clearError,
  errorPrefix,
  idPrefix,
  disabled,
}: FieldListProps) {
  return visibleFields(fields, values, context).map(({ field, scope }) => {
    const errorKey = errorPrefix + field.key
    const id = `${idPrefix}-${errorKey}`.replace(/[^\w-]/g, '-')
    return (
      <ScreenFieldItem
        key={field.key}
        field={field}
        value={values[field.key] ?? null}
        onChange={(value) => {
          onChange(field.key, value)
          clearError(errorKey)
        }}
        id={id}
        error={errors[errorKey]}
        disabled={disabled}
        scope={scope}
        renderRow={
          field.kind === ScreenFieldKind.ROWS
            ? (row, setRow) => (
                <FieldList
                  fields={rowFieldsFor(field, row.id)}
                  values={row.values}
                  context={rowContext(field, row.id)}
                  onChange={(key, value) => setRow({ ...row, values: { ...row.values, [key]: value } })}
                  errors={errors}
                  clearError={clearError}
                  errorPrefix={rowErrorKey(errorPrefix, field, row.id)}
                  idPrefix={idPrefix}
                  disabled={disabled}
                />
              )
            : undefined
        }
      />
    )
  })
}
